import get from 'lodash.get';

import { emptyArray } from 'ui-components/default-props/empty';
import { LOCAL_DELIVERY } from '../../enums/csw-types';
import { DimensionUnit } from '../../enums/dimension-unit';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { CustomsService } from '../../services/customs-service';
import { OrderDetailsHelperService } from '../../services/order-details-helper-service';
import Global from '../../store/global-selectors';
import { LogService } from '../../services/log-service';
import { RATE_BROWSER, CUSTOMERS_CREATE_LABEL } from '../../constants/fulfillment-plan-ids';
import { getSellerFulfillmentProviderIdFromShipFromId } from './csw.fulfillment-plan-to-csw';

export const DEFAULT_OPTIONS = {
  dryIce: null,
  includeReturnLabel: null,
  collectOnDelivery: null,
  dangerousGoods: null,
  dangerousGoodsLegacy: null,
  dangerousGoodsPurolator: null,
  consequentialLoss: null,
  emailNotification: null,
  smsNotification: null,
  safeplace: null,
  billToAccount: null,
  shipperRelease: false,
  saturdayDelivery: false,
  containsAlcohol: false,
  nonMachinable: false,
  deliveredDutyPaid: false,
  holdForPickup: false,
  specialHandling: false,
  dontNotifyMarketplace: false,
  saturdayGuarantee: false,
  localCollect: false,
  goGreenPlus: false,
  regulatedContentType: '',
  fedExShipperLoadAndCount: '',
  fedExBookingConfirmation: '',
  fragile: false,
  deliveryAsAddressed: false,
  returnAfterFirstAttempt: false,
};

export const buildUniqueArrayOfItems = (items) => {
  const ids = [...new Set(items.map((item) => item.salesOrderItemId))];
  return ids.map((id) => {
    const matchingItem = items.find((item) => item.salesOrderItemId === id);
    return {
      salesOrderItemId: id,
      quantity: matchingItem.quantity,
      returnReasonId: matchingItem.returnReasonId,
    };
  });
};

const mapSalesOrderIdOntoItems = (state, items = [], fulfillmentPlanId) => {
  const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, {
    fulfillmentPlanId,
  });
  const isReturn = Global.selectCSWIsReturn(state, { fulfillmentPlanId });
  const relatedSalesOrderIds = get(fulfillmentPlan, 'salesOrdersFulfilledIds', emptyArray);
  const relatedSalesOrders = relatedSalesOrderIds.map((salesOrderId) => Global.selectEntitiesSalesOrderById(state, { salesOrderId }));
  const salesOrderItems = relatedSalesOrders.reduce((soItems, so) => [...soItems, ...get(so, 'items', emptyArray)], []);

  return items
    .map((i) => ({
      salesOrderItemId: OrderDetailsHelperService.assignSalesOrderItemId(i, salesOrderItems),
      quantity: i.quantity,
      returnReasonId: isReturn ? i.returnReasonId || null : undefined,
    }))
    .filter((item) => item.salesOrderItemId);
};

const getShipTo = (state, fulfillmentPlanId, isReturn = false, shippingConfigurationFromState = {}) => {
  if (fulfillmentPlanId === RATE_BROWSER || fulfillmentPlanId === CUSTOMERS_CREATE_LABEL || isReturn) {
    // fulfillmentPlanId === RATE_BROWSER || fulfillmentPlanId === CUSTOMERS_CREATE_LABEL
    // User is creating a label from a place where we have no fulfillmentPlan/salesOrder context,
    // so just rely on the provided shippingConfiguration values.
    // isReturn
    // We are allowing users to adjust the shipTo for returns now, so rely on the provided shippingConfiguration
    // values in this case too, so we don't force the return shipTo to be the same as the salesOrder.
    return shippingConfigurationFromState.shipTo;
  }

  // Here we should be able to rely on the fulfillmentPlan's shipTo values...
  const fulfillmentPlanShipTo = Global.selectEntitiesFulfillmentPlanProperty(state, { fulfillmentPlanId, property: 'shipTo' });

  // ...but the callers of this function used to derive shipTo values from the fulfillmentPlan's associated salesOrders,
  // so we'll be extra careful and use that as a fallback.
  const [firstSalesOrder] =
    Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
      fulfillmentPlanIds: [fulfillmentPlanId],
    }) || emptyArray;
  const [salesOrderShipTo] = firstSalesOrder?.shipTos || emptyArray;

  if (!fulfillmentPlanShipTo) {
    // Log the anomalies.
    LogService.logInfo(`Missing fulfillmentPlan.shipTo when building rating request for fulfillmentPlanId: ${fulfillmentPlanId}`, {
      salesOrderId: firstSalesOrder?.salesOrderId,
      shippingConfigurationFromState,
    });
  }

  return fulfillmentPlanShipTo || salesOrderShipTo || shippingConfigurationFromState.shipTo;
};

const buildShippingConfigurationForRequest = (state, { fulfillmentPlanId }) => {
  const cswTypeId = Global.selectCSWTypeId(state, { fulfillmentPlanId });
  const isReturn = Global.selectCSWIsReturn(state, { fulfillmentPlanId });
  const shippingConfigurationFromState = Global.selectCSWShippingConfiguration(state, { fulfillmentPlanId }) || {};
  const sellerDimensionUnit = Global.selectSellerDefaultDimensionUnit(state);
  const isLocalDelivery = cswTypeId && cswTypeId.includes(LOCAL_DELIVERY);
  const shouldIncludeInsuranceValue =
    shippingConfigurationFromState.insuranceProvider &&
    shippingConfigurationFromState.insuranceProvider !== InsuranceProviders.None.name &&
    !isLocalDelivery;
  const insuranceProvider = shouldIncludeInsuranceValue ? shippingConfigurationFromState.insuranceProvider : InsuranceProviders.None.name;
  const serviceCollectionGuid = shippingConfigurationFromState.serviceCollectionGuid;
  const serviceId = serviceCollectionGuid ? null : shippingConfigurationFromState.serviceId;
  const sellerLabelProviderId = serviceCollectionGuid ? null : shippingConfigurationFromState.sellerLabelProviderId;

  // TODO: Once we are able to use the package-dropdown-with-dimensions, that should handle in patching up package props
  const packages = (Global.selectCSWPackages(state, { fulfillmentPlanId }) || emptyArray)
    .filter((pkg) => !!pkg)
    .map((p) => {
      const dimensionUnit =
        get(p, 'dimensions.unit', DimensionUnit.Unknown) === DimensionUnit.Unknown ? sellerDimensionUnit : get(p, 'dimensions.unit');
      const dimensions = get(p, 'dimensions', {}) || {};
      return {
        ...p,
        packageId: undefined,
        packageTypeId: p.packageId ? p.packageId.split('-')[0] : null,
        dimensions: {
          ...dimensions,
          length: dimensions.length || 0,
          width: dimensions.width || 0,
          height: dimensions.height || 0,
          unit: dimensionUnit,
        },
        insuredValue: {
          ...p.insuredValue,
          value: shouldIncludeInsuranceValue ? get(p, 'insuredValue.value', 0) : 0,
        },
      };
    });
  const overrideItems = Global.selectCSWOverrideShipmentItems(state, {
    fulfillmentPlanId,
  });
  let items;
  if (Array.isArray(overrideItems)) {
    items = overrideItems.filter((i) => i.selected);
  } else {
    items = Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'items',
    });
  }

  const itemsWithSalesOrderItemIds = mapSalesOrderIdOntoItems(state, items, fulfillmentPlanId);
  const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, {
    fulfillmentPlanId,
  });
  const salesOrderId =
    isReturn && fulfillmentPlanId && fulfillmentPlanId.replace
      ? fulfillmentPlanId.replace('return-', '')
      : get(fulfillmentPlan, 'salesOrdersFulfilledIds', emptyArray)[0];
  const shipTo = getShipTo(state, fulfillmentPlanId, isReturn, shippingConfigurationFromState);
  const originalService = Global.selectSellerServiceById(state, { serviceId });

  const stateOptionIncludeReturnLabel = get(shippingConfigurationFromState, 'options.includeReturnLabel');
  const includeReturnLabel = stateOptionIncludeReturnLabel || DEFAULT_OPTIONS.includeReturnLabel;
  const orderStoreId = Global.selectEntitiesSalesOrderProperty(state, {
    salesOrderId,
    property: 'store.storeGuid',
  });
  const {
    deliveryEmailNotificationTemplateId,
    options,
    packingSlipTemplateId,
    pickup,
    shipDate,
    shipFromId,
    shipmentEmailNotificationTemplateId,
  } = shippingConfigurationFromState;

  // Ensure that ship from value exists, if it doesn't, use default shipFrom
  const shipFroms = Global.selectSellerShipFroms(state);
  const fulfillmentProviderWarehouses = Global.selectSellerFulfillmentProviderWarehouses(state);
  const allShipFroms = shipFroms.concat(fulfillmentProviderWarehouses);
  const defaultShipFromId = Global.selectSellerDefaultShipFromId(state);
  const verifiedShipFromId = allShipFroms.find((shipFrom) => shipFrom.shipFromId === shipFromId) ? shipFromId : defaultShipFromId;

  const defaultCustomsData = Global.selectSellerDefaultCustomData(state);
  const defaultCurrency = Global.selectSellerDefaultCurrency(state);
  const defaultWeightUnit = Global.selectSellerDefaultWeightUnit(state);
  const customs = CustomsService.fillCustomsWithDefaults(
    Global.selectCSWCustoms(state, { fulfillmentPlanId }),
    defaultCustomsData,
    defaultCurrency,
    defaultWeightUnit,
  );

  const getShipmentEmailNotificationTemplateId = () => {
    if (shipmentEmailNotificationTemplateId !== undefined) {
      return shipmentEmailNotificationTemplateId;
    }
    return Global.selectSellerShipmentTemplateIdByStoreId(state, {
      storeGuid: orderStoreId,
    });
  };
  const getDeliveryEmailNotificationTemplateId = () => {
    if (deliveryEmailNotificationTemplateId !== undefined) {
      return deliveryEmailNotificationTemplateId;
    }
    return Global.selectSellerDeliveryTemplateIdByStoreId(state, {
      storeGuid: orderStoreId,
    });
  };

  return {
    ...shippingConfigurationFromState,
    insuranceProvider,
    serviceId,
    sellerLabelProviderId,
    shipFromId: verifiedShipFromId,
    shipTo,
    shipDate,
    packingSlipTemplateId:
      packingSlipTemplateId ||
      Global.selectSellerPackingSlipIdByStoreId(state, {
        storeGuid: orderStoreId,
      }) ||
      null,
    shipmentEmailNotificationTemplateId: getShipmentEmailNotificationTemplateId(),
    deliveryEmailNotificationTemplateId: getDeliveryEmailNotificationTemplateId(),
    options: {
      ...DEFAULT_OPTIONS,
      ...options,
      includeReturnLabel,
    },
    packages,
    pickup: isLocalDelivery ? pickup : undefined,
    items: buildUniqueArrayOfItems(itemsWithSalesOrderItemIds),
    customs,
    isReturn,
    shippingConfigurationHash: fulfillmentPlan?.shippingConfigurationHash,
    carrierId: originalService?.carrierId,
  };
};

export const buildSaveShippingConfigurationAndRateRequest = (state, { fulfillmentPlanId }) => {
  return {
    fulfillmentPlanId,
    ...buildShippingConfigurationForRequest(state, { fulfillmentPlanId }),
  };
};

export const buildCreateLabelRequest = (state, { fulfillmentPlanId }) => {
  return {
    fulfillmentPlanId,
    items: [],
    ...buildShippingConfigurationForRequest(state, { fulfillmentPlanId }),
  };
};

const createReturnRequest = ({ shippingConfiguration, items, isAutoRMA, rmaNumber, salesOrderId, note }) => {
  return {
    returnConfiguration: {
      ...shippingConfiguration,
      shipDate: undefined,
      customs: undefined,
      items: undefined,
    },
    items,
    rmaNumber: isAutoRMA ? null : rmaNumber,
    salesOrderId,
    note,
  };
};

export const buildOrderDetailsCanvasReturnRequest = (state, { fulfillmentPlanId, salesOrderId, items, isAutoRMA, rmaNumber, note }) => {
  const shippingConfiguration = buildShippingConfigurationForRequest(state, {
    fulfillmentPlanId,
  });

  return createReturnRequest({
    shippingConfiguration,
    items,
    isAutoRMA,
    rmaNumber,
    salesOrderId,
    note,
  });
};

const getSellerFulfillmentProviderId = (state, props) => {
  const entitiesSellerFulfillmentProviderId = Global.selectEntitiesFulfillmentPlanSellerFulfillmentProviderID(state, props);
  const fallBackSellerFulfillmentProviderId = entitiesSellerFulfillmentProviderId || undefined;
  const cswConfig = Global.selectCSWShippingConfiguration(state, props);
  if (!cswConfig?.shipFromId) {
    return fallBackSellerFulfillmentProviderId;
  }

  const cswSellerFulfillmentProviderId = getSellerFulfillmentProviderIdFromShipFromId(cswConfig.shipFromId);
  return cswSellerFulfillmentProviderId || fallBackSellerFulfillmentProviderId;
};

const buildExternalFulfillmentConfiguration = (state, props, cswShippingConfig) => {
  const { fulfillmentPlanId } = props;
  const shipTo = getShipTo(state, fulfillmentPlanId);
  const sellerFulfillmentProviderId = getSellerFulfillmentProviderId(state, props);

  return {
    sellerFulfillmentProviderId,
    fulfillmentServiceId: Global.selectCSWFulfillmentServiceId(state, props),
    shipTo,
    additionalOptions: cswShippingConfig?.options,
  };
};

export const buildExternalFulfillmentSaveAndRateRequest = (state, props) => {
  const { fulfillmentPlanId } = props;
  const cswShippingConfig = Global.selectCSWShippingConfiguration(state, { fulfillmentPlanId }) || {};
  return {
    fulfillmentPlanId,
    packingSlipTemplateId: Global.selectCSWPackingSlipTemplateId(state, props),
    shipmentEmailNotificationTemplateId: Global.selectCSWShipmentTemplateId(state, props),
    deliveryEmailNotificationTemplateId: Global.selectCSWDeliveryTemplateId(state, props),
    externalFulfillmentConfiguration: buildExternalFulfillmentConfiguration(state, props, cswShippingConfig),
  };
};
